// Module 5 deferred — Jink-schedule editor for the Threats pane.
//
// Editable rows {t_s, duration_s, accel_mps2, axis} with a live
// sparkline showing |jink|(t). Round-trips via PATCH on the threat's
// evasive.jink_schedule list. Only surfaces when the selected threat
// has mode == "evasive".
import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import { Button } from "@/components/ui/button";
import SpinnerButton from "@/components/SpinnerButton";
import {
    LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip,
    CartesianGrid,
} from "recharts";
import { sim } from "@/api/endpoints";

const AXIS_OPTIONS = [
    { id: "lateral", label: "Lateral" },
    { id: "vertical", label: "Vertical" },
];

function computeSparkline(schedule, horizon = 30, step = 0.25) {
    const n = Math.ceil(horizon / step);
    const out = new Array(n);
    for (let i = 0; i < n; i += 1) {
        const t = i * step;
        let a = 0;
        for (const row of schedule) {
            const t0 = Number(row.t_s || 0);
            const dur = Number(row.duration_s || 0);
            if (t >= t0 && t < t0 + dur) {
                a = Math.max(a, Math.abs(Number(row.accel_mps2 || 0)));
            }
        }
        out[i] = { t, a };
    }
    return out;
}

export default function JinkScheduleEditor({ threat, readOnly, onSaved }) {
    const [rows, setRows] = useState(() =>
        (threat?.evasive?.jink_schedule || []).map((r) => ({
            t_s: r.t_s,
            duration_s: r.duration_s,
            accel_mps2: r.accel_mps2,
            axis: r.axis || "lateral",
        })),
    );
    const [busy, setBusy] = useState(false);

    const add = () => {
        const last = rows[rows.length - 1] || { t_s: 0, duration_s: 0.5, accel_mps2: 15, axis: "lateral" };
        setRows([...rows, {
            t_s: Number(last.t_s) + Number(last.duration_s) + 1,
            duration_s: 0.5,
            accel_mps2: 15,
            axis: last.axis,
        }]);
    };
    const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
    const update = (i, key, val) => setRows(rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

    const spark = useMemo(() => computeSparkline(rows), [rows]);

    const save = async () => {
        setBusy(true);
        try {
            const schedule = rows.map((r) => ({
                t_s: Number(r.t_s),
                duration_s: Number(r.duration_s),
                accel_mps2: Number(r.accel_mps2),
                axis: r.axis,
            }));
            await sim.threats.patch(threat.id, { evasive: { jink_schedule: schedule } });
            toast.success("Jink schedule saved");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="space-y-3 p-3 border border-ux-border-1 rounded-md bg-ux-bg-1"
            data-testid="jink-schedule-editor"
        >
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-ux-text-2 font-mono uppercase tracking-wider">
                    Jink schedule ({rows.length} events)
                </div>
                {!readOnly && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={add}
                        data-testid="jink-add"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add event
                    </Button>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="text-[11px] text-ux-text-3 font-mono">
                    No jink events — add one to model evasive manoeuvres.
                </div>
            ) : (
                <table className="w-full text-[12px] font-mono">
                    <thead>
                        <tr className="text-left text-ux-text-3 border-b border-ux-border-1">
                            <th className="py-1.5">t (s)</th>
                            <th className="py-1.5">dur (s)</th>
                            <th className="py-1.5">accel (m/s²)</th>
                            <th className="py-1.5">axis</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-ux-border-1/40">
                                <td className="py-1 pr-2">
                                    <Input
                                        type="number"
                                        step="0.1"
                                        value={r.t_s}
                                        readOnly={readOnly}
                                        onChange={(e) => update(i, "t_s", Number(e.target.value))}
                                        className="h-7 bg-ux-bg-2 text-[12px] tabular-nums"
                                        data-testid={`jink-t-${i}`}
                                    />
                                </td>
                                <td className="py-1 pr-2">
                                    <Input
                                        type="number"
                                        step="0.1"
                                        value={r.duration_s}
                                        readOnly={readOnly}
                                        onChange={(e) => update(i, "duration_s", Number(e.target.value))}
                                        className="h-7 bg-ux-bg-2 text-[12px] tabular-nums"
                                        data-testid={`jink-dur-${i}`}
                                    />
                                </td>
                                <td className="py-1 pr-2">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={r.accel_mps2}
                                        readOnly={readOnly}
                                        onChange={(e) => update(i, "accel_mps2", Number(e.target.value))}
                                        className="h-7 bg-ux-bg-2 text-[12px] tabular-nums"
                                        data-testid={`jink-accel-${i}`}
                                    />
                                </td>
                                <td className="py-1 pr-2 w-[110px]">
                                    <Select
                                        value={r.axis}
                                        onValueChange={(v) => update(i, "axis", v)}
                                        disabled={readOnly}
                                    >
                                        <SelectTrigger
                                            className="h-7 bg-ux-bg-2 text-[12px] font-mono"
                                            data-testid={`jink-axis-${i}`}
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                                            {AXIS_OPTIONS.map((o) => (
                                                <SelectItem key={o.id} value={o.id} className="text-[12px] font-mono">
                                                    {o.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="py-1 text-right">
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => remove(i)}
                                            className="text-ux-text-3 hover:text-ux-danger"
                                            data-testid={`jink-remove-${i}`}
                                            aria-label="Remove jink event"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* |jink|(t) sparkline */}
            <div className="h-[90px]" data-testid="jink-sparkline">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={spark} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#1e293b" strokeDasharray="2 2" />
                        <XAxis dataKey="t" type="number" stroke="#64748b" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="a" stroke="#64748b" tick={{ fontSize: 10 }} />
                        <RTooltip
                            contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11 }}
                            formatter={(v) => [v.toFixed(1), "|jink| m/s²"]}
                            labelFormatter={(l) => `t=${l.toFixed(2)}s`}
                        />
                        <Line
                            type="stepAfter"
                            dataKey="a"
                            stroke="#14b8a6"
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {!readOnly && (
                <SpinnerButton size="sm" onClick={save} loading={busy} testId="jink-save">
                    Save jink schedule
                </SpinnerButton>
            )}
        </div>
    );
}
